import React from 'react';
import { ALERT_LEVELS } from '../consts';
import css from './UsageAlert.css';

const formatNumber = num => {
    return Math.round(num).toLocaleString();
};

const buildGentleTooltip = (alert, translateFunc) => {
    const { expectedRunRatePercent, actualCapUsage, runRatePercent } = alert;
    const currentRunRate = formatNumber((runRatePercent / 100) * expectedRunRatePercent);
    const expectedRunRate = formatNumber(expectedRunRatePercent);
    const eventsProcessed = formatNumber(actualCapUsage);

    return (
        <div>
            <div className={css.tooltipTitle}>
                {translateFunc('STATIC.PAGES.ACCOUNT_PLAN.ALERTS.GENTLE_TOOLTIP_TITLE')}
            </div>
            <div className={css.tooltipSectionTitle}>
                {translateFunc('STATIC.PAGES.ACCOUNT_PLAN.ALERTS.GENTLE_TOOLTIP_CURRENT_STATUS')}
            </div>
            <ul className={css.tooltipList}>
                <li>
                    {translateFunc('STATIC.PAGES.ACCOUNT_PLAN.ALERTS.GENTLE_TOOLTIP_CURRENT_RUN_RATE', { currentRunRate })}
                </li>
                <li>
                    {translateFunc('STATIC.PAGES.ACCOUNT_PLAN.ALERTS.GENTLE_TOOLTIP_EXPECTED_RUN_RATE', { expectedRunRate })}
                </li>
                <li>
                    {translateFunc('STATIC.PAGES.ACCOUNT_PLAN.ALERTS.GENTLE_TOOLTIP_EVENTS_PROCESSED', { eventsProcessed })}
                </li>
            </ul>
        </div>
    );
};

const buildModerateTooltip = (alert, translateFunc) => {
    const { expectedRunRatePercent, actualCapUsage, runRatePercent, cap } = alert;
    const totalEvents = formatNumber(actualCapUsage);
    const capFormatted = formatNumber(cap);
    const currentRunRate = formatNumber((runRatePercent / 100) * expectedRunRatePercent);
    const requiredRunRate = formatNumber(expectedRunRatePercent);
    const bufferCap = formatNumber(cap * 1.1);

    return (
        <div>
            <div className={css.tooltipTitle}>
                {translateFunc('STATIC.PAGES.ACCOUNT_PLAN.ALERTS.MODERATE_TOOLTIP_TITLE')}
            </div>
            <div className={css.tooltipSectionTitle}>
                {translateFunc('STATIC.PAGES.ACCOUNT_PLAN.ALERTS.MODERATE_TOOLTIP_CURRENT_STATUS')}
            </div>
            <ul className={css.tooltipListWithMargin}>
                <li>
                    {translateFunc('STATIC.PAGES.ACCOUNT_PLAN.ALERTS.MODERATE_TOOLTIP_TOTAL_EVENTS', { totalEvents, cap: capFormatted })}
                </li>
                <li>
                    {translateFunc('STATIC.PAGES.ACCOUNT_PLAN.ALERTS.MODERATE_TOOLTIP_CURRENT_RUN_RATE', { currentRunRate })}
                </li>
                <li>
                    {translateFunc('STATIC.PAGES.ACCOUNT_PLAN.ALERTS.MODERATE_TOOLTIP_REQUIRED_RUN_RATE', { requiredRunRate })}
                </li>
            </ul>
            <div>
                {translateFunc('STATIC.PAGES.ACCOUNT_PLAN.ALERTS.MODERATE_TOOLTIP_BUFFER', { bufferCap })}
            </div>
        </div>
    );
};

const buildHarshTooltip = (alert, translateFunc) => {
    const { expectedRunRatePercent, actualCapUsage, runRatePercent } = alert;
    const currentRunRate = formatNumber((runRatePercent / 100) * expectedRunRatePercent);
    const expectedRunRate = formatNumber(expectedRunRatePercent);
    const eventsProcessed = formatNumber(actualCapUsage);

    return (
        <div>
            <div className={css.tooltipTitleBold}>
                {translateFunc('STATIC.PAGES.ACCOUNT_PLAN.ALERTS.HARSH_TOOLTIP_TITLE')}
            </div>
            <div className={css.tooltipSectionTitle}>
                {translateFunc('STATIC.PAGES.ACCOUNT_PLAN.ALERTS.HARSH_TOOLTIP_USAGE_VIOLATION')}
            </div>
            <ul className={css.tooltipList}>
                <li>
                    {translateFunc('STATIC.PAGES.ACCOUNT_PLAN.ALERTS.GENTLE_TOOLTIP_CURRENT_RUN_RATE', { currentRunRate })}
                </li>
                <li>
                    {translateFunc('STATIC.PAGES.ACCOUNT_PLAN.ALERTS.GENTLE_TOOLTIP_EXPECTED_RUN_RATE', { expectedRunRate })}
                </li>
                <li>
                    {translateFunc('STATIC.PAGES.ACCOUNT_PLAN.ALERTS.GENTLE_TOOLTIP_EVENTS_PROCESSED', { eventsProcessed })}
                </li>
            </ul>
        </div>
    );
};

const buildBlockedTooltip = (alert, translateFunc) => {
    const { actualCapUsage, cap } = alert;
    const events = formatNumber(actualCapUsage);
    const threshold = formatNumber(cap);
    const overagePercent = formatNumber(((actualCapUsage - cap) / cap) * 100);

    return (
        <div>
            <div className={css.tooltipTitleBold}>
                {translateFunc('STATIC.PAGES.ACCOUNT_PLAN.ALERTS.BLOCKED_TOOLTIP_TITLE')}
            </div>
            <div className={css.tooltipSectionTitle}>
                {translateFunc('STATIC.PAGES.ACCOUNT_PLAN.ALERTS.BLOCKED_TOOLTIP_STATUS')}
            </div>
            <ul className={css.tooltipListNoStyle}>
                <li>
                    {translateFunc('STATIC.PAGES.ACCOUNT_PLAN.ALERTS.BLOCKED_TOOLTIP_ATTRIBUTION_ACTIVE')}
                </li>
                <li>
                    {translateFunc('STATIC.PAGES.ACCOUNT_PLAN.ALERTS.BLOCKED_TOOLTIP_POSTBACKS_ACTIVE')}
                </li>
                <li>
                    {translateFunc('STATIC.PAGES.ACCOUNT_PLAN.ALERTS.BLOCKED_TOOLTIP_PLATFORM_SUSPENDED')}
                </li>
            </ul>
            <div className={css.tooltipSectionTitle}>
                {translateFunc('STATIC.PAGES.ACCOUNT_PLAN.ALERTS.BLOCKED_TOOLTIP_USAGE_AT_SUSPENSION')}
            </div>
            <ul className={css.tooltipList}>
                <li>
                    {translateFunc('STATIC.PAGES.ACCOUNT_PLAN.ALERTS.BLOCKED_TOOLTIP_EVENTS', { events, overagePercent, threshold })}
                </li>
            </ul>
        </div>
    );
};

const TOOLTIP_BUILDER_MAP = {
    [ALERT_LEVELS.BLOCKED]: buildBlockedTooltip,
    [ALERT_LEVELS.HARSH_WARNING]: buildHarshTooltip,
    [ALERT_LEVELS.MODERATE_WARNING]: buildModerateTooltip,
    [ALERT_LEVELS.GENTLE_WARNING]: buildGentleTooltip,
};

export const buildTooltip = (alertLevel, alert, translateFunc) => {
    const builder = TOOLTIP_BUILDER_MAP[alertLevel];
    return builder ? builder(alert, translateFunc) : null;
};

export const formatAlertNumber = formatNumber;

